import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Empty, Input, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { getApiError } from '../../api/client';
import type { Firm } from '../../api/types';
import { useIsMobile } from '../../lib/responsive';
import { useFirmaCurenta } from './FirmaCurentaContext';
import { FirmaFormDrawer } from './FirmaFormDrawer';
import { deleteFirma, fetchFirmeList } from './api';

const { Title } = Typography;
const PAGE_SIZE = 20;

/**
 * `/firme` — the CRUD table plus the onboarding fallback FirmaCurentaProvider
 * redirects to when there are zero active firms (`?onboarding=1`, or a
 * direct visit to /firme with none): same `Empty` state, Drawer opened
 * automatically instead of requiring a click.
 */
export function FirmeListPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { firme: activeFirme, firmaId, setFirmaId, isLoading: isLoadingContext } = useFirmaCurenta();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  // Gated on `!isLoadingContext` so a direct visit (no `?onboarding=1` yet)
  // doesn't flash the Empty state during the split second before the
  // FirmaCurenta firms query resolves and `activeFirme` is still `[]`.
  const isOnboarding = !isLoadingContext && (searchParams.get('onboarding') === '1' || activeFirme.length === 0);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);

  // Handle the ANAF OAuth callback redirect
  // (?efacturaConnected=1&companyId=… or ?efacturaError=…): show a message,
  // open that firm's drawer on the e-Factura tab, then strip the params so a
  // refresh doesn't replay it. Ref-guarded to run once per mount.
  const efacturaHandledRef = useRef(false);
  useEffect(() => {
    if (efacturaHandledRef.current) return;
    const connected = searchParams.get('efacturaConnected');
    const efacturaError = searchParams.get('efacturaError');
    if (!connected && !efacturaError) return;
    efacturaHandledRef.current = true;

    const companyId = searchParams.get('companyId');
    if (connected) {
      message.success('Firma a fost conectată la ANAF e-Factura.');
      if (companyId) {
        setEditingId(companyId);
        setInitialTab('efactura');
        setDrawerOpen(true);
      }
    } else if (efacturaError) {
      message.error(`Conectarea la ANAF a eșuat: ${efacturaError}`);
    }

    const next = new URLSearchParams(searchParams);
    next.delete('efacturaConnected');
    next.delete('efacturaError');
    next.delete('companyId');
    setSearchParams(next, { replace: true });
  }, [searchParams, message, setSearchParams]);

  // Auto-opens the create Drawer the first time this page is genuinely in
  // an onboarding state (per the brief). A ref guard (rather than an empty
  // dependency array) means it fires exactly once *after* loading actually
  // resolves, instead of racing the context's initial `isLoading: true`
  // render — and never fires again afterward, so it doesn't fight a user
  // who closes the Drawer while still onboarding.
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (isOnboarding && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setEditingId(null);
      setDrawerOpen(true);
    }
  }, [isOnboarding]);

  const { data, isLoading } = useQuery({
    queryKey: ['firms', 'list', { page, pageSize: PAGE_SIZE, search }],
    queryFn: () => fetchFirmeList({ page, pageSize: PAGE_SIZE, search: search || undefined }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFirma,
    onSuccess: () => {
      message.success('Firma a fost ștearsă.');
      queryClient.invalidateQueries({ queryKey: ['firms'] });
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Firma nu a putut fi ștearsă.');
    },
  });

  function openCreate() {
    setEditingId(null);
    setInitialTab(undefined);
    setDrawerOpen(true);
  }
  function openEdit(id: string) {
    setEditingId(id);
    setInitialTab(undefined);
    setDrawerOpen(true);
  }
  function closeDrawer() {
    setDrawerOpen(false);
    setEditingId(null);
    setInitialTab(undefined);
    if (searchParams.get('onboarding') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('onboarding');
      setSearchParams(next, { replace: true });
    }
  }

  const columns: ColumnsType<Firm> = [
    { title: 'Denumire', dataIndex: 'name' },
    {
      title: 'CUI',
      dataIndex: 'cui',
      render: (cui: string, row) => (row.isVatPayerToday ? `RO${cui}` : cui),
    },
    { title: 'Reg. Com.', dataIndex: 'regCom', render: (v: string | null) => v ?? '—' },
    {
      title: 'TVA',
      dataIndex: 'isVatPayerToday',
      render: (v: boolean) => (v ? <Tag color="green">Plătitor TVA</Tag> : <Tag>Neplătitor</Tag>),
    },
    {
      title: 'Stare',
      dataIndex: 'isActive',
      render: (v: boolean) => (v ? <Tag color="green">Activă</Tag> : <Tag>Inactivă</Tag>),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row.id)}>
            Editează
          </Button>
          <Button size="small" disabled={row.id === firmaId} onClick={() => setFirmaId(row.id)}>
            {row.id === firmaId ? 'Firmă curentă' : 'Setează ca firmă curentă'}
          </Button>
          <Popconfirm
            title="Ștergi firma?"
            description="Firma poate fi ștearsă doar dacă nu are date asociate (clienți, produse, facturi etc.)."
            onConfirm={() => deleteMutation.mutate(row.id)}
          >
            <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === row.id}>
              Șterge
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Firme</Title>
      {isOnboarding && !drawerOpen ? (
        <Empty description="Nu există nicio firmă activă. Adaugă prima firmă pentru a începe facturarea.">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Adaugă firmă
          </Button>
        </Empty>
      ) : (
        <>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <Input.Search
              placeholder="Caută după denumire sau CUI"
              allowClear
              style={{ width: isMobile ? '100%' : 320 }}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
            <Button type="primary" icon={<PlusOutlined />} block={isMobile} onClick={openCreate}>
              Adaugă firmă
            </Button>
          </div>
          <Table<Firm>
            rowKey="id"
            columns={columns}
            dataSource={data?.items}
            loading={isLoading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data?.total ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
          />
        </>
      )}
      <FirmaFormDrawer open={drawerOpen} editingId={editingId} onClose={closeDrawer} initialTab={initialTab} />
    </div>
  );
}
